import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { propertiesApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Home, MapPin, Users, Bed, Bath, Euro, Settings2 } from 'lucide-react';

const PROPERTY_TYPES = [
  { value: 'appartement', label: 'Appartement' },
  { value: 'maison', label: 'Maison' },
  { value: 'villa', label: 'Villa' },
  { value: 'studio', label: 'Studio' },
  { value: 'loft', label: 'Loft' },
  { value: 'chalet', label: 'Chalet' },
];

const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1644057501622-dfa7dd26dbfb?crop=entropy&cs=srgb&fm=jpg&w=800&q=80';

export default function PropertiesPage() {
  const { loadProperties: reloadContext } = useOutletContext();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    property_type: 'appartement',
    bedrooms: 1,
    bathrooms: 1,
    max_guests: 2,
    base_price: 100,
    cleaning_fee: 50,
    description: '',
    image_url: '',
  });

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    setLoading(true);
    try {
      const response = await propertiesApi.getAll();
      setProperties(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      property_type: 'appartement',
      bedrooms: 1,
      bathrooms: 1,
      max_guests: 2,
      base_price: 100,
      cleaning_fee: 50,
      description: '',
      image_url: '',
    });
    setEditingProperty(null);
  };

  const handleEdit = (property) => {
    navigate(`/admin/proprietes/${property.id}/edit`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseInt(formData.bathrooms),
        max_guests: parseInt(formData.max_guests),
        base_price: parseFloat(formData.base_price),
        cleaning_fee: parseFloat(formData.cleaning_fee),
      };

      if (editingProperty) {
        await propertiesApi.update(editingProperty.id, data);
        toast.success('Propriété mise à jour');
      } else {
        await propertiesApi.create(data);
        toast.success('Propriété créée');
      }
      setDialogOpen(false);
      resetForm();
      loadProperties();
      reloadContext();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette propriété ? Toutes les données liées seront perdues.')) return;
    try {
      await propertiesApi.delete(id);
      toast.success('Propriété supprimée');
      loadProperties();
      reloadContext();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="properties-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight">Propriétés</h1>
          <p className="text-muted-foreground mt-1 text-sm">Gérez vos biens</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow" data-testid="add-property-button">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-card border-border max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">{editingProperty ? 'Modifier la propriété' : 'Nouvelle propriété'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Appartement Centre-Ville" required data-testid="property-name-input" />
              </div>

              <div className="space-y-2">
                <Label>Adresse *</Label>
                <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="123 Rue Exemple, Paris" required data-testid="property-address-input" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={formData.property_type} onValueChange={(v) => setFormData({ ...formData, property_type: v })}>
                    <SelectTrigger data-testid="property-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Capacité</Label>
                  <Input type="number" min="1" value={formData.max_guests} onChange={(e) => setFormData({ ...formData, max_guests: e.target.value })} data-testid="property-guests-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Chambres</Label>
                  <Input type="number" min="0" value={formData.bedrooms} onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })} data-testid="property-bedrooms-input" />
                </div>
                <div className="space-y-2">
                  <Label>Salles de bain</Label>
                  <Input type="number" min="0" value={formData.bathrooms} onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })} data-testid="property-bathrooms-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prix / nuit (€)</Label>
                  <Input type="number" step="0.01" min="0" value={formData.base_price} onChange={(e) => setFormData({ ...formData, base_price: e.target.value })} data-testid="property-price-input" />
                </div>
                <div className="space-y-2">
                  <Label>Frais de ménage (€)</Label>
                  <Input type="number" step="0.01" min="0" value={formData.cleaning_fee} onChange={(e) => setFormData({ ...formData, cleaning_fee: e.target.value })} data-testid="property-cleaning-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>URL Image</Label>
                <Input type="url" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="https://..." data-testid="property-image-input" />
              </div>

              <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="property-submit-button">
                {editingProperty ? 'Mettre à jour' : 'Créer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Properties Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(2)].map((_, i) => (
            <Card key={i} className="bg-card border-border/40 animate-pulse">
              <div className="h-40 bg-muted rounded-t-sm" />
              <CardContent className="pt-4"><div className="h-5 bg-muted rounded w-3/4 mb-2" /><div className="h-4 bg-muted rounded w-1/2" /></CardContent>
            </Card>
          ))}
        </div>
      ) : properties.length === 0 ? (
        <Card className="bg-card border-border/40">
          <CardContent className="py-12 text-center">
            <Home className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-heading font-medium mb-2">Aucune propriété</h3>
            <p className="text-muted-foreground text-sm mb-4">Ajoutez votre première propriété pour commencer</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="h-4 w-4 mr-2" />Ajouter une propriété
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {properties.map((property) => (
            <Card key={property.id} className="bg-card border-border/40 overflow-hidden hover:border-primary/30 transition-colors group" data-testid={`property-card-${property.id}`}>
              <div className="relative h-36 md:h-40 overflow-hidden">
                <img src={property.image_url || DEFAULT_IMAGE} alt={property.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <Badge className="absolute top-3 left-3 bg-primary/90 text-primary-foreground capitalize text-xs">{property.property_type}</Badge>
              </div>
              <CardContent className="pt-3">
                <h3 className="font-heading font-medium mb-1 text-sm md:text-base">{property.name}</h3>
                <div className="flex items-center text-xs text-muted-foreground mb-3">
                  <MapPin className="h-3 w-3 mr-1" />{property.address}
                </div>
                
                <div className="grid grid-cols-3 gap-2 py-3 border-t border-border/40 text-xs">
                  <div className="flex items-center gap-1"><Bed className="h-3 w-3 text-muted-foreground" />{property.bedrooms}</div>
                  <div className="flex items-center gap-1"><Bath className="h-3 w-3 text-muted-foreground" />{property.bathrooms}</div>
                  <div className="flex items-center gap-1"><Users className="h-3 w-3 text-muted-foreground" />{property.max_guests}</div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-border/40">
                  <div className="flex items-center gap-1 text-primary font-semibold tabular-nums text-sm">
                    <Euro className="h-3 w-3" />{property.base_price}<span className="text-muted-foreground font-normal text-xs">/nuit</span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(property)} className="hover:text-primary hover:bg-primary/10 h-8 w-8" data-testid={`edit-property-${property.id}`}>
                      <Settings2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(property.id)} className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8" data-testid={`delete-property-${property.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
